import { parseSkillAuthoringResult } from './skill-ai';
import type { SkillAuthoringResult } from './skill-ai';
import { getToolOutputForToolCall } from '../chat/ai-sdk-ui-messages';
import type { ToolCall } from '../chat/tool-calls';

const SAVED_TO_DRAFT_DESCRIPTION = 'Test it from the skill page, then publish it.';

const FAILED_SAVE_TITLES = {
  blocked: 'Could not save skill',
  failed: 'Could not save skill',
  interrupted: 'Skill save interrupted',
} satisfies Record<Extract<SkillAuthoringResult, { message: string }>['status'], string>;

export interface SkillAuthoringPresentation {
  description: string | null;
  title: string;
  skill?: { id: string; name: string };
}

export function getSkillAuthoringPresentation({
  approvalReason,
  approved,
  loading = false,
  output,
  toolCall,
}: {
  toolCall: Extract<ToolCall, { type: 'createSkill' | 'updateSkill' }>;
  approvalReason?: string;
  approved?: boolean;
  loading?: boolean;
  output?: unknown;
}): SkillAuthoringPresentation {
  const result = parseSkillAuthoringResult(output);
  if (result) return getResultPresentation(result);

  const isCreate = toolCall.type === 'createSkill';
  const description = toolCall.name ? `Skill: ${toolCall.name}.` : null;
  if (approved === false)
    return {
      description: approvalReason || description,
      title: getDeclinedTitle({ approvalReason, isCreate }),
    };
  if (approved === true)
    return { description, title: loading ? 'Saving skill...' : 'Skill save interrupted' };
  if (toolCall.preparing)
    return {
      description: isCreate ? 'Writing the skill.' : 'Writing the changes.',
      title: isCreate ? 'Preparing skill...' : 'Preparing skill changes...',
    };
  if (!toolCall.skillId) return { description, title: 'Waiting on your approval' };
  return {
    description,
    skill: { id: toolCall.skillId, name: toolCall.name || 'Untitled skill' },
    title: getSavedTitle({ isCreate, loading }),
  };
}

export function getSkillAuthoringToolOutput(
  uiParts: unknown[] | null | undefined,
  toolCallId: string
) {
  const result = getToolOutputForToolCall(uiParts, toolCallId);
  if (result?.state === 'output-error')
    return { message: result.errorText ?? 'The skill could not be saved.', status: 'failed' };
  return result?.output;
}

function getResultPresentation(result: SkillAuthoringResult): SkillAuthoringPresentation {
  switch (result.status) {
    case 'created':
    case 'updated':
      return {
        description: SAVED_TO_DRAFT_DESCRIPTION,
        skill: { id: result.skillId, name: result.name },
        title: result.status === 'created' ? 'Created skill' : 'Saved to draft',
      };
    case 'blocked':
    case 'failed':
    case 'interrupted':
      return { description: result.message, title: FAILED_SAVE_TITLES[result.status] };
  }
}

function getDeclinedTitle({
  approvalReason,
  isCreate,
}: {
  approvalReason: string | undefined;
  isCreate: boolean;
}) {
  if (approvalReason) return 'Skill proposal declined';
  return isCreate ? 'Skill skipped' : 'Skill changes skipped';
}

function getSavedTitle({ isCreate, loading }: { isCreate: boolean; loading: boolean }) {
  if (loading) return 'Saving skill...';
  return isCreate ? 'Created skill' : 'Saved to draft';
}
